import { useArticleList } from '@/services/query/article.query';
import {
  ArticleEntity,
  ArticleListProps,
  ArticleType,
  GetArticleListParams,
} from '@/services/types/article.type';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

export interface ListContainerProps {
  type: ArticleType;
  limit?: number;
  /** 게시글 목록 조회에 추가로 넘길 파라미터 (예: academyId) */
  extraListParams?: Partial<GetArticleListParams>;
  /** false면 목록 첫 글을 헤더로 쓰지 않음 */
  showHeaderItem?: boolean;
  render: (
    props: ArticleListProps & { headerItem?: ArticleEntity },
  ) => React.ReactNode;
}

export function ListContainer({
  type,
  limit,
  extraListParams,
  showHeaderItem = true,
  render,
}: ListContainerProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // URL에서 페이지 파라미터 가져오기 (기본값: 1)
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const [page, setPage] = useState(initialPage);
  const [headerItem, setHeaderItem] = useState<ArticleEntity | undefined>(
    undefined,
  );

  const listParams = useMemo(
    () => ({
      page,
      limit,
      type,
      ...extraListParams,
    }),
    [page, limit, type, extraListParams],
  );

  // URL의 page 쿼리와 동기화 (외부에서 page 제거·변경 시 반영)
  const pageFromUrl = searchParams.get('page');
  useEffect(() => {
    const nextPage = parseInt(pageFromUrl || '1', 10);
    if (!Number.isNaN(nextPage)) setPage(nextPage);
  }, [pageFromUrl]);

  // 실제 데이터 패칭 로직
  const { data, isLoading, error } = useArticleList(listParams);

  // 첫 번째 페이지 로드 시에만 헤더 아이템 설정
  useEffect(() => {
    if (!showHeaderItem) return;
    if (page === 1 && data?.data.articles?.[0] && !headerItem) {
      setHeaderItem(data.data.articles[0]);
    }
  }, [data, page, headerItem, showHeaderItem]);

  // 페이지 변경 핸들러
  const handlePageChange = (newPage: number) => {
    setPage(newPage);

    // URL 업데이트
    const params = new URLSearchParams(searchParams.toString());
    if (newPage === 1) {
      params.delete('page'); // 1페이지일 때는 쿼리 파라미터 제거
    } else {
      params.set('page', newPage.toString());
    }

    const newUrl = params.toString() ? `?${params.toString()}` : '';
    router.replace(newUrl, { scroll: false });
  };

  const articles = data?.data.articles ?? [];
  const totalCount = data?.data.totalCount ?? 0;
  const totalPages = limit && limit > 0 ? Math.ceil(totalCount / limit) : 0;

  // console.log('headerItem:', headerItem?.title);
  // console.log(
  //   'current items:',
  //   articles.map((item) => item.title),
  // );

  if (isLoading) return <div>로딩중...</div>;
  if (error) return <div>에러 발생!</div>;

  return (
    <section className="desktop:flex justify-center">
      <div className="desktop:web-w desktop:w-full">
        {render({
          items: articles,
          page,
          totalPages,
          onPageChange: handlePageChange,
          headerItem: showHeaderItem ? headerItem : undefined,
        })}
      </div>
    </section>
  );
}
